/**
 * SWISS School Portal - API Configuration
 * =========================================
 * Configuration for the SWISS School Portal API endpoints
 * and AWS Cognito Hosted UI (federated Google sign-in).
 */

// Environment-based API URL
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  'https://6rojje3wmd.execute-api.ap-south-1.amazonaws.com/production';

// Portal-specific endpoints
const PORTAL_API_URL = process.env.REACT_APP_PORTAL_API_URL ||
  `${API_BASE_URL}/portal`;

// Portal hostname (used to build Cognito callback URLs)
const PORTAL_ORIGIN = process.env.REACT_APP_PORTAL_ORIGIN ||
  (typeof window !== 'undefined' && window.location.origin) ||
  'https://swiss.mysafespaces.org';

export const config = {
  // API Endpoints
  api: {
    baseUrl: API_BASE_URL,
    portalUrl: PORTAL_API_URL,
  },

  // SWISS admin backend (DynamoDB-backed Lambda + HTTP API)
  // All admin CRUD goes here. Frontend sends Cognito id_token in
  // Authorization header; HTTP API verifies it.
  admin: {
    apiUrl: process.env.REACT_APP_ADMIN_API_URL ||
      'https://dq6b58bc54.execute-api.ap-south-1.amazonaws.com',
  },

  // LearnWorlds (Thrive Academy) proxy — server-side Lambda that holds
  // the LW client_secret and brokers calls. Frontend sends Cognito
  // id_token in Authorization header; HTTP API verifies it.
  learnworlds: {
    proxyUrl: process.env.REACT_APP_LW_PROXY_URL ||
      'https://1lcm4uu886.execute-api.ap-south-1.amazonaws.com',
  },

  // AWS Cognito Hosted UI (federated identity provider for Google sign-in)
  //
  // Fleet-SSO migration: as of the cutover, SWISS uses the SHARED MSS
  // Identity pool (ap-south-1_oPkAmgCMd, "login.mysafespaces.org") instead
  // of its own dedicated pool — see infrastructure/README.md's "Cognito
  // migration" section. The old pool (ap-south-1_vmO9uXCm6) stays alive,
  // untouched, as the migration Lambda's verification source and as a
  // rollback fallback for a fixed window after cutover; DO NOT reuse those
  // legacy values below once the shared client id is set.
  //
  // userPoolClientId default below is a placeholder — set the real
  // SwissAppClient id (from the Interns repo's mss-identity-stack.yaml
  // deploy output) via REACT_APP_USER_POOL_CLIENT_ID before this is live.
  cognito: {
    region: process.env.REACT_APP_COGNITO_REGION || 'ap-south-1',
    userPoolId: process.env.REACT_APP_USER_POOL_ID || 'ap-south-1_oPkAmgCMd',
    userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '5bgbhf8ifmnei73nnh45po85t7',
    domain: process.env.REACT_APP_COGNITO_DOMAIN || 'login.mysafespaces.org',
    scopes: ['email', 'openid', 'profile'],
    identityProvider: 'Google',
    redirectSignIn: process.env.REACT_APP_REDIRECT_SIGNIN || `${PORTAL_ORIGIN}/auth/callback`,
    redirectSignOut: process.env.REACT_APP_REDIRECT_SIGNOUT || `${PORTAL_ORIGIN}/login`,
  },

  // Google OAuth (referenced for documentation; Cognito brokers the actual flow).
  // SWISS-dedicated Google OAuth client — not shared with admin/HR portals.
  google: {
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID ||
      '593330013164-acou89jmvnrrp96gv00f1atgrdh26fm4.apps.googleusercontent.com',
    allowedDomains: (process.env.REACT_APP_ALLOWED_DOMAINS || 'mysafespaces.org,mysafespaces.net')
      .split(',')
      .map((d) => d.trim().toLowerCase()),
  },

  // Super admin allowlist — bypasses all permission checks; can manage MSS admins.
  superAdminEmails: [
    'hjerome@mysafespaces.org',
    'sonia@mysafespaces.org',
  ],

  // MSS admin allowlist — can manage schools and school admins (reset passwords,
  // onboard new school admins) but cannot add or remove other MSS admins.
  mssAdminEmails: [
    'deepika@mysafespaces.org',
    'amogha@mysafespaces.org',
  ],

  // Token storage keys
  jwt: {
    storageKey: 'swiss_portal_token',
    schoolStorageKey: 'swiss_portal_school',
    googleUserKey: 'swiss_portal_google_user',
    refreshTokenKey: 'swiss_portal_refresh_token',
    pkceVerifierKey: 'swiss_portal_pkce_verifier',
    oauthStateKey: 'swiss_portal_oauth_state',
  },

  // Support Contact
  support: {
    email: 'swiss@mysafespaces.org',
    phone: '+91 80626 89525',
    crisisHotline: '+91 80626 89525',
    iCall: '9152987821',
    vandrevala: '1860-2662-345',
  },

  // Portal URL
  portalUrl: 'swiss.mysafespaces.org',
};

export default config;
